import { useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mic, Square, Loader2, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function VoiceRecorder({
  onLogged,
  onUpgrade,
  voiceUrl = "/foods/log-voice",
  textUrl = "/foods/log-text",
  textKey = "description",
  placeholder = 'e.g. "a can of coke and a glazed donut"',
  idleLabel = "Tap & tell me what you ate",
}) {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const [textMode, setTextMode] = useState(false);
  const [text, setText] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const handleError = (err) => {
    const status = err.response?.status;
    const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
    toast.error(detail);
    if (status === 402) onUpgrade?.();
  };

  const notifyLogged = (data) => {
    if (data.transcript) toast.success(`Logged: "${data.transcript}"`);
    else toast.success("Logged");
    onLogged?.(data);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await uploadAudio(blob);
      };
      mediaRef.current = recorder;
      recorder.start();
      setState("recording");
    } catch {
      toast.error("Microphone access denied. Use text entry instead.");
      setTextMode(true);
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && state === "recording") {
      setState("processing");
      mediaRef.current.stop();
    }
  };

  const uploadAudio = async (blob) => {
    const form = new FormData();
    form.append("file", blob, "recording.webm");
    try {
      const { data } = await api.post(voiceUrl, form, { headers: { "Content-Type": "multipart/form-data" } });
      notifyLogged(data);
    } catch (err) {
      handleError(err);
    } finally {
      setState("idle");
    }
  };

  const submitText = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setState("processing");
    try {
      const { data } = await api.post(textUrl, { [textKey]: text.trim() });
      setText("");
      notifyLogged(data);
    } catch (err) {
      handleError(err);
    } finally {
      setState("idle");
    }
  };

  const label = state === "recording" ? "Tap to stop" : state === "processing" ? "Analyzing…" : idleLabel;

  return (
    <div className="flex flex-col items-center gap-5" data-testid="voice-recorder">
      {!textMode ? (
        <>
          <div className="relative flex items-center justify-center">
            {state === "recording" && (
              <>
                <span className="absolute w-40 h-40 rounded-full bg-primary/30 pulse-ring" aria-hidden="true" />
                <span className="absolute w-40 h-40 rounded-full bg-primary/20 pulse-ring" style={{ animationDelay: "0.6s" }} aria-hidden="true" />
              </>
            )}
            <button
              onClick={state === "recording" ? stopRecording : state === "idle" ? startRecording : undefined}
              disabled={state === "processing"}
              className={`relative w-36 h-36 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 focus:outline-none focus:ring-4 focus:ring-primary/40 ${
                state === "recording" ? "bg-destructive" : "bg-primary hover:bg-primary/90"
              }`}
              data-testid="voice-record-button"
            >
              {state === "processing" ? (
                <Loader2 className="w-12 h-12 text-white animate-spin" />
              ) : state === "recording" ? (
                <Square className="w-12 h-12 text-white" fill="white" />
              ) : (
                <Mic className="w-14 h-14 text-white" />
              )}
            </button>
          </div>
          <p className="font-heading font-bold text-lg text-foreground text-center" data-testid="voice-status-label">{label}</p>
          <button onClick={() => setTextMode(true)} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-colors" data-testid="switch-to-text-btn">
            <Keyboard className="w-4 h-4" /> Type instead
          </button>
        </>
      ) : (
        <form onSubmit={submitText} className="w-full max-w-md space-y-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="rounded-xl h-12" data-testid="text-food-input" />
          <div className="flex gap-2">
            <Button type="submit" disabled={state === "processing"} className="flex-1 rounded-full h-11 font-bold" data-testid="text-log-submit-btn">
              {state === "processing" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log it"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setTextMode(false)} className="rounded-full h-11" data-testid="switch-to-voice-btn">
              <Mic className="w-4 h-4" />
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
